import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/meta";
import { CTABand, ParallaxImage, Reveal, SectionDivider } from "../components/Chrome";

const EASE = [0.22, 1, 0.36, 1];

export default function Subsite({ slug }) {
  const { data } = useQuery({
    queryKey: ["content", slug],
    queryFn: async () => (await api.get(`/content/${slug}`)).data,
  });
  const c = data?.data;
  usePageMeta(c ? `${c.hero.title} — Klyx™` : "Klyx™", c?.hero?.sub);
  if (!c) return <div data-testid={`${slug}-loading`} className="min-h-screen bg-ink" />;

  return (
    <div data-testid={`subsite-${slug}-page`}>
      {/* Hero */}
      <section className="relative pt-40 pb-24 lg:pt-52 lg:pb-32 overflow-hidden spotlight">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              data-testid={`${slug}-hero-eyebrow`}
              className="font-mono text-xs tracking-[0.3em] uppercase text-mauve mb-6"
            >
              {c.hero.eyebrow}
            </motion.p>
            <div className="overflow-hidden">
              <motion.h1
                initial={{ y: "105%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.15, ease: EASE }}
                data-testid={`${slug}-hero-title`}
                className="font-heading font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] text-silver text-balance"
              >
                {c.hero.title}
              </motion.h1>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
              className="mt-8 text-base sm:text-lg text-zinc-200 leading-relaxed max-w-xl"
            >
              {c.hero.sub}
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: EASE }}
            className="lg:col-span-5"
          >
            <div className="aspect-[4/3] overflow-hidden border border-white/10">
              <img src={c.hero.image} alt={c.hero.title} className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sections */}
      {c.sections.map((s, i) => (
        <section
          key={`${s.title}-${i}`}
          data-testid={`${slug}-section-${i}`}
          className={i % 2 === 1 ? "bg-elevated" : "bg-surface"}
        >
          <SectionDivider />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2 lg:col-start-8" : ""}`}>
              <div className="aspect-[4/3] overflow-hidden border border-white/10">
                <ParallaxImage src={s.image} alt={s.title} className="w-full h-full" speed={0.08} />
              </div>
            </Reveal>
            <Reveal delay={0.1} className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
              <div className="flex items-center gap-4 mb-5">
                <span className="h-[2px] w-10 bg-mauve shrink-0" />
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-mauve">
                  Chapter {String(i + 1).padStart(2, "0")}
                </p>
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-silver text-balance">
                {s.title}
              </h2>
              <p className="mt-6 text-base sm:text-lg text-zinc-200 leading-relaxed">{s.body}</p>
              {s.points && (
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Plus size={14} className="text-mauve mt-0.5 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          </div>
        </section>
      ))}

      <CTABand cta={c.cta} />
    </div>
  );
}
